/**
 * Utility functions for error handling
 */

/**
 * Standard error handling for API requests
 * @param {Error} error - The error that occurred
 * @param {Object} responseData - Additional error data from the API response
 * @param {Object} options - Additional options for handling the error
 * @returns {Object} Standardized error object with message, field errors, etc.
 */
export const handleApiError = (error, responseData = null, options = {}) => {
  // Default options
  const defaultOptions = {
    showConsoleErrors: true,
    includeStackTrace: process.env.NODE_ENV !== 'production'
  };
  
  const opts = { ...defaultOptions, ...options };
  
  // Create a standardized error object
  const errorResult = {
    message: error.message || 'An unexpected error occurred',
    status: 500,
    fieldErrors: {},
    isPermissionError: false,
    isAuthError: false,
    isServerError: false,
    isNetworkError: false,
    originalError: opts.includeStackTrace ? error : null
  };
  
  // Add response data if available
  if (responseData) {
    if (responseData.message) {
      errorResult.message = responseData.message;
    }
    
    if (responseData.status) {
      errorResult.status = responseData.status;
    }
    
    if (responseData.errors && typeof responseData.errors === 'object') {
      errorResult.fieldErrors = responseData.errors;
    }
    
    if (responseData.required && Array.isArray(responseData.required)) {
      responseData.required.forEach(field => {
        errorResult.fieldErrors[field] = `${field.replace('_', ' ')} is required`;
      });
    }
  }
  
  // Determine error type based on status code or message
  if (errorResult.status === 401) {
    errorResult.isAuthError = true;
    errorResult.message = 'Authentication error. Please log in again.';
  } else if (errorResult.status === 403) {
    errorResult.isPermissionError = true;
    errorResult.message = 'Permission denied. You do not have access to this resource.';
  } else if (errorResult.status >= 500) {
    errorResult.isServerError = true;
  }
  
  // Check for network errors
  if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
    errorResult.isNetworkError = true;
    errorResult.message = 'Network error. Please check your connection.';
  }
  
  // Log errors to console if needed
  if (opts.showConsoleErrors) {
    if (errorResult.isPermissionError) {
      console.error('Permission error:', errorResult.message);
    } else if (errorResult.isAuthError) {
      console.error('Authentication error:', errorResult.message);
    } else if (errorResult.isServerError) {
      console.error('Server error:', errorResult.message);
    } else if (errorResult.isNetworkError) {
      console.error('Network error:', errorResult.message);
    } else {
      console.error('API error:', errorResult.message);
    }
    
    if (opts.includeStackTrace && error.stack) {
      console.error('Error stack:', error.stack);
    }
  }
  
  return errorResult;
};

/**
 * Format permission errors for display
 * @param {Object} error - Error object from handleApiError
 * @returns {string} User-friendly error message
 */
export const formatPermissionError = (error) => {
  if (!error) {
    return 'An unknown error occurred';
  }
  
  if (error.isPermissionError) {
    return `Permission denied: You don't have the required permissions for this action. Please contact your administrator if you need access.`;
  }
  
  if (error.isAuthError) {
    return 'Your session has expired. Please log in again.';
  }
  
  // Generic error message if not a permission error
  return error.message || 'An unexpected error occurred';
};

/**
 * Check if an error is related to permissions
 * @param {Error|Object} error - Error to check
 * @returns {boolean} True if it's a permission-related error
 */
export const isPermissionError = (error) => {
  if (!error) return false;
  
  // Check for standardized error object
  if (error.isPermissionError) return true;
  
  // Check for status code
  if (error.status === 403) return true;
  
  // Check for common permission error messages
  const permissionKeywords = [
    'permission denied',
    'not authorized',
    'insufficient permissions',
    'access denied',
    'forbidden'
  ];
  
  const message = (error.message || '').toLowerCase();
  return permissionKeywords.some(keyword => message.includes(keyword));
};

export default {
  handleApiError,
  formatPermissionError,
  isPermissionError
};